import * as React from 'react'
import { useContext } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form'
import * as FormWizardStyles from '../../../../components/organisms/FormWizard/styles'
import { FormWizardHandlers } from '../../../../components/organisms/FormWizard'
import { CreateContractsContext } from '../index'
import PaymentDetailsForm from './paymentDetailsForm'
import { convertNumberToCurrencyFormat } from '../../../../utils/convertNumberToCurrencyFormat'
import { useContextPage } from '../contextPage'
import { SelectField } from 'components/atoms/SelectField'
import { Contract } from '../../../../domain/usecases/Contracts'
import { AppearFromRight } from '../../../../components/templates/ApperFromLeft'
import { MultiSelectField } from 'components/atoms/MultiSelectField'
import formatStringToCNPJ from '../../../../utils/formatStringToCNPJ'
import { Person } from '../../../../domain/models/PersonsModel'
import { yupResolver } from '@hookform/resolvers/yup'
import step5_schema from './step5.schema'
import { TextInput } from 'components/atoms/TextInput'
import { ContractPaymentPart } from 'domain/models/PaymentPartsModel'
import { useToast } from '../../../../hooks/useToast'

const PAYMENT_TYPE_OPTIONS = [
  { label: 'Sinal', value: 'Sinal' },
  { label: 'Entrada', value: 'Entrada' },
  { label: 'Parcelas', value: 'Parcelas' },
  { label: 'Financiamento Bancário', value: 'Financiamento Bancário' },
  { label: 'Parcelas Mensais', value: 'Parcelas Mensais' },
  { label: 'Parcelas Intermediárias', value: 'Parcelas Intermediárias' },
  { label: 'Parcelas Trimestrais', value: 'Parcelas Trimestrais' },
  { label: 'Parcelas Quinquemestrais', value: 'Parcelas Quinquemestrais' },
  { label: 'Parcelas Semestrais', value: 'Parcelas Semestrais' },
  { label: 'Parcelas Anuais', value: 'Parcelas Anuais' },
  { label: 'Pré-Chaves', value: 'Pré-Chaves' },
  { label: 'Entrega das Chaves', value: 'Entrega das Chaves' },
  { label: 'Taxa de Enxoval', value: 'Taxa de Enxoval' }
]

export type ICreateContractsFormStep5 = Pick<
  Contract.CreateParams,
  | 'payment_parts'
  | 'discount_rate'
  | 'uau_vendedor_code'
  | 'uau_modelo_comissao_code'
>
export type ICreateContractBillingPersonId = Pick<
  Contract.CreateParams,
  'billing_person_id'
>

export type errorType = Record<string, boolean>

type SelectOptions = { label: string; value: string }[]

type PaymentMethodItem = {
  id: string
  baseType: string
  displayName: string
}

const generateId = (): string => {
  return Math.random().toString(36).substr(2, 9)
}

export default function Step5({
  nextHandler,
  backHandler
}: FormWizardHandlers) {
  const { state, setState } = useContextPage()
  const { showToast } = useToast()
  const [paymentMethods, setPaymentMethods] = React.useState<
    PaymentMethodItem[]
  >(() => {
    if (state.paymentMethods && Array.isArray(state.paymentMethods)) {
      // Handle legacy string array or new PaymentMethodItem array
      if (typeof state.paymentMethods[0] === 'string') {
        return (state.paymentMethods as string[]).map((method) => ({
          id: generateId(),
          baseType: method,
          displayName: method
        }))
      }
      return state.paymentMethods as PaymentMethodItem[]
    }
    return []
  })
  const [selectedPaymentType, setSelectedPaymentType] =
    React.useState<string>('')
  const [error, setError] = React.useState<errorType>({})
  const [billingPersons, setBillingPersons] = React.useState<SelectOptions>([])
  const { data, setData } = useContext(CreateContractsContext)

  const generateDisplayName = (
    baseType: string,
    existingMethods: PaymentMethodItem[]
  ): string => {
    const sameTypeCount = existingMethods.filter(
      (method) => method.baseType === baseType
    ).length
    if (sameTypeCount === 0) {
      return baseType
    }
    return `${baseType} (${sameTypeCount + 1})`
  }

  const addPaymentMethod = () => {
    if (!selectedPaymentType) return

    const newMethod: PaymentMethodItem = {
      id: generateId(),
      baseType: selectedPaymentType,
      displayName: generateDisplayName(selectedPaymentType, paymentMethods)
    }

    setPaymentMethods([...paymentMethods, newMethod])
    setSelectedPaymentType('')
  }

  const removePaymentMethod = (id: string) => {
    const removedIndex = paymentMethods.findIndex((method) => method.id === id)
    const updatedMethods = paymentMethods.filter((method) => method.id !== id)
    // Os campos do form são indexados (payment_parts[i]); sem tirar o item do
    // array, o componente removido continuava somado e era enviado ao backend.
    const currentParts = getValues('payment_parts') || []
    setValue(
      'payment_parts',
      currentParts.filter((_, index) => index !== removedIndex)
    )
    setPaymentMethods(updatedMethods)
  }

  const valueTraded = convertNumberToCurrencyFormat(data?.value as number)
  const unitValue = convertNumberToCurrencyFormat(
    Number(state.unitSelected?.value)
  )

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors }
  } = useForm<ICreateContractsFormStep5 & ICreateContractBillingPersonId>({
    resolver: yupResolver(step5_schema),
    defaultValues: {
      ...data
    }
  })

  const validatePaymentsParts = (paymentPart: ICreateContractsFormStep5) => {
    const hasError: errorType = {}
    const paymentMethodNames = paymentMethods.map(
      (method) => method.displayName
    )
    const chosenPayments = paymentPart.payment_parts.filter((payment) =>
      paymentMethodNames.includes(payment.name)
    )

    const validate = (
      chosenPayments: ContractPaymentPart[],
      hasError: errorType
    ) => {
      chosenPayments.forEach((paymentPartItem) => {
        const unfilledFields = Object.values(paymentPartItem).filter(
          (value) => !!value === false
        )
        !!unfilledFields.length && (hasError[paymentPartItem.name] = true)
      })
    }

    validate(chosenPayments, hasError)
    return hasError
  }

  const formatCpf = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  React.useEffect(() => {
    setBillingPersons(state.persons?.map(convertPersonToSelectOptions) || [])
  }, [state])

  // Initialize paymentMethods from contract data when editing
  React.useEffect(() => {
    if (data?.payment_parts && data.payment_parts.length > 0) {
      const methods: PaymentMethodItem[] = data.payment_parts.map(
        (part: any) => ({
          id: generateId(),
          baseType: part.name,
          displayName: part.name
        })
      )
      setPaymentMethods(methods)
    }
  }, [data?.payment_parts])

  const convertPersonToSelectOptions = (person: Person) => ({
    label: createPersonLabel(person),
    value: person.id
  })

  const createPersonLabel = (person: Person): string =>
    isIndividual(person) ? individualLabel(person) : juridicalLabel(person)

  const isIndividual = (person: Person) => person.info.cpf

  const individualLabel = (person: Person): string =>
    `${person.info.name} - ${formatCpf(person.info.cpf)}`

  const juridicalLabel = (person: Person): string =>
    `${person.info.business_name} - ${formatStringToCNPJ(person.info.cnpj)}`

  const onClickNext = (submitted: ICreateContractsFormStep5) => {
    // Só os componentes que estão na tela (defesa contra índices órfãos).
    const formData = {
      ...submitted,
      payment_parts: (submitted.payment_parts || []).slice(
        0,
        paymentMethods.length
      )
    }
    if (paymentPartsTotalNotEqualToNegotiatedValue(formData.payment_parts))
      return showToast(
        'O Valor Total de todos os componentes do Pagamento deve ser igual ao Valor Negociado (A taxa de enxoval não esta inclusa no cálculo)',
        'error'
      )
    const errors = validatePaymentsParts(formData)
    if (Object.keys(errors).length) return setError(errors)
    setError({})
    setData!({
      ...data,
      ...formData,
      discount_rate: Number(formData.discount_rate) || 0
    })
    const paymentMethodNames = paymentMethods.map(
      (method) => method.displayName
    )
    setState({ ...state, paymentMethods: paymentMethodNames })
    nextHandler()
  }

  // Comparação em centavos: em ponto flutuante 10 x 1234.56 = 12345.599999999999
  // e o valor batia na tela mas era recusado.
  const toCents = (value: number) => Math.round(Number(value) * 100)

  const paymentPartsTotalNotEqualToNegotiatedValue = (
    payment_parts: ContractPaymentPart[]
  ) =>
    payment_parts.reduce(
      (acc, part) =>
        part.name != 'Taxa de Enxoval' ? acc + toCents(part.total_value) : acc, // Taxa de Enxolval não entra no preço
      0
    ) !== toCents(Number(data?.value))

  const getDefaultValueToPaymentParts = (paymentName: string) => {
    let defaultValuePaymentPart: ContractPaymentPart | undefined

    if (data?.payment_parts) {
      defaultValuePaymentPart = data?.payment_parts.find(
        (payment) => payment.name === paymentName
      )
    }
    return defaultValuePaymentPart
  }

  return (
    <div className="m-3">
      <FormWizardStyles.TabContent>
        <Row className="mt-4">
          <Col md={4}>
            <Controller
              control={control}
              name="billing_person_id"
              render={({ field: { onChange, value } }) => (
                <SelectField
                  data-testid="billing_person_id"
                  type="text"
                  emptyOptionText={'Selecione'}
                  label="Responsável Financeiro"
                  onChange={onChange}
                  value={value}
                  options={billingPersons}
                  defaultValue={data?.billing_person_id || ''}
                  errorMessage={errors.billing_person_id?.message}
                />
              )}
            />
          </Col>
          <Col md={5}>
            <Controller
              control={control}
              name="discount_rate"
              render={({ field: { onChange, value } }) => {
                return (
                  <TextInput
                    type="number"
                    min={0}
                    step={0.1}
                    label="Taxa de desconto para antecipação de valores (%)"
                    onChange={(event) => onChange(event.target.value)}
                    errorMessage={errors.discount_rate?.message}
                    value={value}
                    defaultValue={data?.discount_rate}
                  />
                )
              }}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md={6}>
            <div className="d-flex flex-column">
              <span>Valor do Imóvel (R$)</span>
              <strong>{unitValue}</strong>
            </div>
          </Col>
          <Col md={6} className="mt-2">
            <div className="d-flex flex-column">
              <span>Valor Negociado (R$)</span>
              <strong>{valueTraded}</strong>
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col md={8}>
            <SelectField
              label="Adicionar Componente de Pagamento"
              options={PAYMENT_TYPE_OPTIONS}
              value={selectedPaymentType}
              onChange={(e) => setSelectedPaymentType(e.target.value)}
              emptyOptionText="Selecione um tipo de pagamento"
            />
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Button
              variant="outline-primary"
              onClick={addPaymentMethod}
              disabled={!selectedPaymentType}
              className="mb-3"
            >
              Adicionar
            </Button>
          </Col>
        </Row>
        {paymentMethods.length > 0 && (
          <Row className="mt-3">
            <Col>
              <div className="border rounded p-3">
                <h6 className="mb-3">Componentes de Pagamento Selecionados:</h6>
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
                  >
                    <span>{method.displayName}</span>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removePaymentMethod(method.id)}
                    >
                      Remover
                    </Button>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        )}
        {paymentMethods.map((paymentMethod: PaymentMethodItem, index) => {
          const fieldName = `payment_parts[${index}]`
          return (
            <AppearFromRight key={paymentMethod.id}>
              <PaymentDetailsForm
                defaultValue={getDefaultValueToPaymentParts(
                  paymentMethod.displayName
                )}
                setValue={setValue}
                fieldName={fieldName}
                index={index}
                control={control}
                errors={error}
                paymentMethod={paymentMethod.displayName}
              />
            </AppearFromRight>
          )
        })}
        <Row>
          <Col>
            <FormWizardStyles.ButtonsWrapperSpaced>
              <Button variant="link" onClick={backHandler}>
                Voltar
              </Button>
              <Button
                variant="primary"
                disabled={!paymentMethods.length}
                onClick={handleSubmit(onClickNext)}
              >
                Próximo
              </Button>
            </FormWizardStyles.ButtonsWrapperSpaced>
          </Col>
        </Row>
      </FormWizardStyles.TabContent>
    </div>
  )
}
